import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle, ArrowRight, Search, Phone, Mail, MessageCircle, Home, Users, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { getPublicFaq } from "@/lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1920&q=80";

const CATEGORIES = [
  { key: "all", label: "Toutes", icon: HelpCircle },
  { key: "proprietaire", label: "Propriétaires", icon: Home },
  { key: "voyageur", label: "Voyageurs", icon: Users },
  { key: "general", label: "Général", icon: Settings },
];

const CONTACT_OPTIONS = [
  { icon: Phone, title: "Par téléphone", desc: "Du lundi au samedi, 9h-19h", action: "tel:+33767484313", cta: "+33 7 67 48 43 13" },
  { icon: Mail, title: "Par email", desc: "Réponse sous 24h", action: "mailto:contact@happyhouse-conciergerie.fr", cta: "Envoyer un email" },
  { icon: MessageCircle, title: "Via le chatbot", desc: "Disponible 24h/24", action: null, cta: "Ouvrir le chat" },
];

export default function FaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const res = await getPublicFaq();
        setFaqs(res.data);
      } catch (err) {
        console.error("Error loading FAQs:", err);
      }
    };
    loadFaqs();
  }, []);

  const filteredFaqs = useMemo(() => {
    return faqs.filter((faq) => {
      const matchCategory = activeCategory === "all" || faq.categorie === activeCategory;
      const matchSearch = !searchQuery || faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.reponse.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCategory && matchSearch;
    });
  }, [faqs, activeCategory, searchQuery]);

  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-end overflow-hidden">
        <img src={HERO_IMAGE} alt="FAQ" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-4">
            <HelpCircle className="w-6 h-6 text-[#C9922A]" />
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold">FAQ</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Questions fréquentes
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-white/60 mt-4 max-w-2xl text-lg">
            Tout ce que vous devez savoir sur nos services de conciergerie et de sous-location professionnelle.
          </motion.p>
        </div>
      </section>

      {/* Search + Category Filters */}
      <section className="bg-[#F5F0EB] pt-12 pb-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Bar */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7D6B]" />
            <input
              type="text"
              placeholder="Rechercher une question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border border-[#E0D6CA] bg-white text-[#2D2418] focus:border-[#C9922A] focus:ring-2 focus:ring-[#C9922A]/10 outline-none transition-all shadow-sm"
              data-testid="faq-search"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeCategory === cat.key
                      ? "bg-[#004333] text-white shadow-md"
                      : "bg-white text-[#6B5E50] border border-[#E0D6CA] hover:border-[#C9922A]/40"
                  }`}
                  data-testid={`faq-cat-${cat.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12 bg-[#F5F0EB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div key={activeCategory + searchQuery} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="border border-[#E0D6CA] bg-[#FFFDF9] rounded-xl overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-[#EDE8E1]/50 transition-colors"
                    data-testid={`faq-item-${faq.id}`}
                  >
                    <span className="font-semibold text-[#004333] pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-[#C9922A] flex-shrink-0 transition-transform duration-300 ${openFaq === faq.id ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === faq.id && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                        <div className="px-6 pb-6 text-[#6B5E50] leading-relaxed border-t border-[#E8E0D4] pt-4">{faq.reponse}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredFaqs.length === 0 && faqs.length > 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-[#D4C9B8] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#004333] mb-2">Aucun résultat</h3>
              <p className="text-[#6B5E50] text-sm">Essayez d'autres mots-clés ou sélectionnez une autre catégorie.</p>
              <button onClick={() => { setSearchQuery(""); setActiveCategory("all"); }} className="mt-4 text-[#C9922A] font-medium hover:underline text-sm">
                Réinitialiser les filtres
              </button>
            </div>
          )}

          {faqs.length === 0 && (
            <div className="text-center py-12 text-[#6B5E50]">Chargement des questions...</div>
          )}
        </div>
      </section>

      {/* Besoin d'aide — Contact Options */}
      <section className="py-20 bg-[#EDE8E1]" data-testid="faq-help-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Besoin d'aide ?</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333] mb-3">Vous ne trouvez pas votre réponse ?</h2>
            <p className="text-[#6B5E50] max-w-lg mx-auto">Notre équipe est à votre disposition pour répondre à toutes vos questions.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {CONTACT_OPTIONS.map((option, i) => {
              const Icon = option.icon;
              const Wrapper = option.action ? "a" : "button";
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <Wrapper
                    href={option.action || undefined}
                    className="block w-full bg-[#FFFDF9] rounded-2xl p-6 border border-[#E0D6CA] text-center hover:shadow-lg hover:border-[#C9922A]/30 transition-all duration-300 group"
                    data-testid={`faq-contact-${i}`}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#C9922A]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#C9922A]/20 transition-colors">
                      <Icon className="w-6 h-6 text-[#C9922A]" />
                    </div>
                    <h3 className="font-semibold text-[#004333] mb-1">{option.title}</h3>
                    <p className="text-[#8B7D6B] text-sm mb-4">{option.desc}</p>
                    <span className="text-[#C9922A] text-sm font-semibold">{option.cta}</span>
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#004333]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-4">Vous avez un projet immobilier ?</h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Propriétaire ou voyageur, nous avons une solution adaptée à vos besoins. Contactez-nous pour en discuter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors" data-testid="faq-cta">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/simulateur" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              Simuler mes revenus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
